import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PlusCircle, Edit, Trash2, Upload, Download, Building, Check, AlertCircle, Loader2, Link, ExternalLink, FileText, UserPlus } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import CompanyProfileService, { DatabaseCompanyProfile } from '@/services/CompanyProfileService';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { StorageService } from '@/services/StorageService';
import { supabase } from '@/lib/supabaseClient';
import FileUploader from '@/components/ui/file-uploader';
import { FileUploadService, UploadResult } from '@/services/FileUploadService';

interface TeamMember {
  id: string;
  user_id: string;
  company_id: string;
  designation: string;
  email: string;
  full_name: string;
  created_at: string;
}

interface RegisteredUser {
  id: string;
  email: string;
  full_name: string;
}

interface NewCompanyData {
  company_name: string;
  tagline: string;
  description: string;
  development_stage: 'development' | 'operation';
  industry_type: string;
  website: string;
  contact_email: string;
}

const CompanyManager = () => {
  const [companies, setCompanies] = useState<DatabaseCompanyProfile[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<DatabaseCompanyProfile | null>(null);
  const [newCompanyDialogOpen, setNewCompanyDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<Partial<DatabaseCompanyProfile>>({
    company_name: '',
    tagline: '',
    description: '',
    development_stage: 'development',
    industry_type: '',
    website: '',
    contact_email: '',
    problem_statement: '',
    solution_description: '',
    market_size: '',
    funding_goal: null,
    investment_highlights: [],
    competitive_edge: [],
    target_segment: '',
    growth_drivers: '',
    team_members: null,
    business_model: null,
    traction: null,
    product_offerings: null,
    financial_projections: null,
    entity_type: 'business',
    status: 'active'
  });
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [newMemberDialogOpen, setNewMemberDialogOpen] = useState(false);
  const [newMemberData, setNewMemberData] = useState({
    email: '',
    full_name: '',
    designation: ''
  });
  const { toast } = useToast();
  const { user } = useAuth();
  const [registeredUsers, setRegisteredUsers] = useState<RegisteredUser[]>([]);
  const [newCompanyData, setNewCompanyData] = useState<NewCompanyData>({
    company_name: '',
    tagline: '',
    description: '',
    development_stage: 'development',
    industry_type: '',
    website: '',
    contact_email: ''
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadTeamMembers(selectedCompany.id);
    }
  }, [selectedCompany]);

  useEffect(() => {
    fetchRegisteredUsers();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      setFormData({
        company_name: selectedCompany.company_name,
        tagline: selectedCompany.tagline,
        description: selectedCompany.description,
        development_stage: selectedCompany.development_stage,
        industry_type: selectedCompany.industry_type,
        website: selectedCompany.website,
        contact_email: selectedCompany.contact_email,
        problem_statement: selectedCompany.problem_statement,
        solution_description: selectedCompany.solution_description,
        market_size: selectedCompany.market_size,
        funding_goal: selectedCompany.funding_goal,
        investment_highlights: selectedCompany.investment_highlights,
        competitive_edge: selectedCompany.competitive_edge,
        target_segment: selectedCompany.target_segment,
        growth_drivers: selectedCompany.growth_drivers,
        team_members: selectedCompany.team_members,
        business_model: selectedCompany.business_model,
        traction: selectedCompany.traction,
        product_offerings: selectedCompany.product_offerings,
        financial_projections: selectedCompany.financial_projections,
        entity_type: selectedCompany.entity_type,
        status: selectedCompany.status
      });
    }
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      // Fetch from businesses table instead of company_pitch_decks
      const { data, error } = await supabase
        .from('businesses')
        .select('*')
        .order('business_name');

      if (error) {
        throw error;
      }

      if (!data || data.length === 0) {
        setCompanies([]);
        setSelectedCompany(null);
        setFormData({});
        return;
      }

      // Map business data to company format with all required fields for DatabaseCompanyProfile
      const mappedCompanies = data.map(business => ({
        id: business.id,
        company_name: business.business_name || business.company_name,
        tagline: business.tagline,
        description: business.description,
        development_stage: business.development_stage,
        industry_type: business.industry_type,
        website: business.website,
        contact_email: business.contact_email,
        problem_statement: business.problem_statement,
        solution_description: business.solution_description,
        market_size: business.market_size,
        funding_goal: business.funding_goal,
        investment_highlights: business.investment_highlights,
        competitive_edge: business.competitive_edge,
        target_segment: business.target_segment,
        growth_drivers: business.growth_drivers,
        team_members: business.team_members,
        business_model: business.business_model,
        traction: business.traction,
        product_offerings: business.product_offerings,
        financial_projections: business.financial_projections,
        entity_type: business.entity_type || 'business',
        status: business.status || 'active',
        user_id: business.user_id || '',
        created_at: business.created_at || new Date().toISOString(),
        updated_at: business.updated_at || new Date().toISOString(),
        logo_url: business.logo_url || null,
        pitch_deck_url: business.pitch_deck_url || null
      })) as DatabaseCompanyProfile[];

      setCompanies(mappedCompanies);
      
      // If no company is selected, select the first one
      if (!selectedCompany && mappedCompanies.length > 0) {
        setSelectedCompany(mappedCompanies[0]);
        setFormData(mappedCompanies[0]);
      } else if (selectedCompany) {
        // If a company is selected, verify it still exists in the new data
        const stillExists = mappedCompanies.find(c => c.id === selectedCompany.id);
        if (!stillExists && mappedCompanies.length > 0) {
          setSelectedCompany(mappedCompanies[0]);
          setFormData(mappedCompanies[0]);
        } else if (stillExists) {
          // Update the selected company with fresh data
          setSelectedCompany(stillExists);
          setFormData(stillExists);
        }
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      toast({
        title: "Error",
        description: "Failed to load businesses",
        variant: "destructive"
      });
    }
  };

  const loadTeamMembers = async (companyId: string) => {
    try {
      const { data, error } = await supabase
        .from('company_team_members')
        .select('*')
        .eq('company_id', companyId);

      if (error) throw error;
      setTeamMembers(data || []);
    } catch (error) {
      console.error('Error loading team members:', error);
      toast({
        title: "Error",
        description: "Failed to load team members",
        variant: "destructive"
      });
    }
  };

  const fetchRegisteredUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, full_name')
        .order('full_name');

      if (error) {
        console.error('Error fetching users:', error);
        return;
      }

      setRegisteredUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAddTeamMember = async () => {
    if (!selectedCompany) {
      toast({
        title: "Error",
        description: "No company selected",
        variant: "destructive"
      });
      return;
    }

    // Verify the business exists first
    try {
      const { data: businessExists, error: businessError } = await supabase
        .from('businesses')
        .select('id, business_name, company_name, user_id')
        .eq('id', selectedCompany.id)
        .single();

      if (businessError || !businessExists) {
        console.error('Business not found:', businessError);
        toast({
          title: "Error",
          description: "Selected business not found. Please refresh and try again.",
          variant: "destructive"
        });
        return;
      }
      
      // Check if a corresponding entry exists in company_pitch_decks
      const { data: companyExists, error: companyError } = await supabase
        .from('company_pitch_decks')
        .select('id')
        .eq('id', selectedCompany.id)
        .maybeSingle();
        
      // If no entry exists in company_pitch_decks, create one
      if (!companyExists) {
        const companyName = businessExists.company_name || businessExists.business_name;
        const { error: insertError } = await supabase
          .from('company_pitch_decks')
          .insert({
            id: selectedCompany.id,  // Use the same ID as the business
            company_name: companyName,
            entity_type: 'business',
            status: 'active',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            user_id: businessExists.user_id || ''
          });
          
        if (insertError) {
          console.error('Error creating company entry:', insertError);
          toast({
            title: "Error",
            description: "Failed to create company entry. Please try again.",
            variant: "destructive"
          });
          return;
        }
      }

      if (!newMemberData.email || !newMemberData.full_name || !newMemberData.designation) {
        toast({
          title: "Error",
          description: "Please fill in all fields",
          variant: "destructive"
        });
        return;
      }

      // Check if the user exists in profiles
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', newMemberData.email)
        .single();

      if (userError) {
        console.error('Error finding user:', userError);
        toast({
          title: "Error",
          description: "User not found with this email. Please ensure the user has registered first.",
          variant: "destructive"
        });
        return;
      }

      // Check if already a team member
      const { data: existingMember, error: checkError } = await supabase
        .from('company_team_members')
        .select('id')
        .eq('company_id', selectedCompany.id)
        .eq('email', newMemberData.email)
        .maybeSingle(); // Use maybeSingle() instead of single()

      if (existingMember) {
        toast({
          title: "Error",
          description: "This user is already a team member",
          variant: "destructive"
        });
        return;
      }

      // Add the team member
      const { error: insertError } = await supabase
        .from('company_team_members')
        .insert({
          company_id: selectedCompany.id,
          user_id: userData.id,
          designation: newMemberData.designation,
          email: newMemberData.email,
          full_name: newMemberData.full_name
        });

      if (insertError) {
        console.error('Error adding team member:', insertError);
        throw insertError;
      }

      toast({
        title: "Success",
        description: "Team member added successfully"
      });

      await loadTeamMembers(selectedCompany.id);
      setNewMemberDialogOpen(false);
      setNewMemberData({ email: '', full_name: '', designation: '' });
    } catch (error) {
      console.error('Error adding team member:', error);
      toast({
        title: "Error",
        description: "Failed to add team member. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleRemoveTeamMember = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from('company_team_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Team member removed successfully"
      });

      if (selectedCompany) {
        loadTeamMembers(selectedCompany.id);
      }
    } catch (error) {
      console.error('Error removing team member:', error);
      toast({
        title: "Error",
        description: "Failed to remove team member",
        variant: "destructive"
      });
    }
  };

  const handleInputChange = (field: string, value: any) => {
    // For text fields that should preserve newlines and bullet points
    if (['business_model', 'financial_projections', 'traction', 'description', 'problem_statement', 'solution_description'].includes(field)) {
      // Process bullet points and newlines
      let processedValue = value;
      if (typeof value === 'string') {
        // If the text starts with a bullet point, replace it with a dash
        processedValue = value.replace(/^[•]\s*/g, '- ');
        // Replace any bullet points after newlines with dashes
        processedValue = processedValue.replace(/\n[•]\s*/g, '\n- ');
      }
      setFormData(prev => ({ ...prev, [field]: processedValue }));
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatTextContent = (text: string | null | undefined): string | null => {
    if (!text) return null;
    return text.replace(/\/n/g, '\n');
  };

  const handleSave = async () => {
    try {
      if (!selectedCompany) {
        toast({
          title: "Error",
          description: "No company selected",
          variant: "destructive"
        });
        return;
      }

      // Helper function to safely parse JSON or return the original value
      const safeParseJSON = (value: any) => {
        if (!value) return null;
        if (typeof value === 'object') return value;
        try {
          return JSON.parse(value);
        } catch (e) {
          // If it's not valid JSON, return it as is
          return value;
        }
      };

      // Helper function to handle string arrays
      const processStringArray = (value: string[] | string | null | undefined): string[] => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (typeof value === 'string') return value.split('\n').filter(Boolean);
        return [];
      };

      // Process the data, handling both JSON and non-JSON fields
      const processedData = {
        ...formData,
        // Fields that should be JSON objects
        business_model: safeParseJSON(formData.business_model),
        traction: safeParseJSON(formData.traction),
        team_members: safeParseJSON(formData.team_members),
        product_offerings: safeParseJSON(formData.product_offerings),
        financial_projections: safeParseJSON(formData.financial_projections),
        // Fields that should be arrays
        investment_highlights: processStringArray(formData.investment_highlights),
        competitive_edge: processStringArray(formData.competitive_edge)
      };

      const updatedCompany = await CompanyProfileService.updateCompanyProfile(
        selectedCompany.id,
        processedData
      );

      // Update the companies list with the updated company
      setCompanies(prevCompanies => 
        prevCompanies.map(company => 
          company.id === selectedCompany.id ? updatedCompany : company
        )
      );

      setSelectedCompany(updatedCompany);
      setEditMode(false);

      toast({
        title: "Success",
        description: "Company profile updated successfully",
      });
    } catch (error) {
      console.error('Update error:', error);
      console.error('Error updating company:', error);
      toast({
        title: "Error",
        description: "Failed to update company profile",
        variant: "destructive"
      });
    }
  };

  const handleAddCompany = async () => {
    try {
      if (!newCompanyData.company_name || !newCompanyData.tagline) {
        toast({
          title: "Error",
          description: "Company name and tagline are required",
          variant: "destructive"
        });
        return;
      }

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        toast({
          title: "Error",
          description: "You must be logged in to add a company",
          variant: "destructive"
        });
        return;
      }

      // Make sure we're using the correct field names
      const dataToInsert = {
        company_name: newCompanyData.company_name,
        tagline: newCompanyData.tagline,
        description: newCompanyData.description,
        development_stage: newCompanyData.development_stage,
        industry_type: newCompanyData.industry_type,
        website: newCompanyData.website,
        contact_email: newCompanyData.contact_email,
        user_id: userData.user.id,
        entity_type: 'business',
        status: 'active'
      };

      const { data, error } = await supabase
        .from('company_pitch_decks')
        .insert(dataToInsert)
        .select()
        .single();

      if (error) {
        console.error('Insert error:', error);
        throw error;
      }

      toast({
        title: "Success",
        description: "Company added successfully"
      });

      setNewCompanyDialogOpen(false);
      setNewCompanyData({
        company_name: '',
        tagline: '',
        description: '',
        development_stage: 'development',
        industry_type: '',
        website: '',
        contact_email: ''
      });
      
      // Refresh the companies list
      loadCompanies();
    } catch (error) {
      console.error('Error adding company:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add company",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="container mx-auto py-6">
      <Tabs defaultValue="business-partners">
        <div className="flex justify-between items-center mb-6">
          <TabsList>
            <TabsTrigger value="business-partners">Business Partners</TabsTrigger>
            <TabsTrigger value="pitch-decks">Pitch Decks</TabsTrigger>
          </TabsList>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => window.location.href = '/admin'}>
              <Building className="h-4 w-4 mr-2" />
              Manage Businesses
            </Button>
          </div>
        </div>

        <TabsContent value="business-partners">
          <Card>
            <CardHeader>
              <CardTitle>Business Partners Management</CardTitle>
              <CardDescription>Manage company staff and their roles for businesses created in the Businesses section</CardDescription>
            </CardHeader>
            <CardContent>
              {selectedCompany ? (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">{selectedCompany.company_name} - Team</h3>
                    <div className="flex gap-2">
                      <Select 
                        value={selectedCompany.id} 
                        onValueChange={(value) => {
                          const company = companies.find(c => c.id === value);
                          if (company) {
                            setSelectedCompany(company);
                            setFormData(company);
                          }
                        }}
                      >
                        <SelectTrigger className="w-[240px]">
                          <SelectValue placeholder="Select company" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map(company => (
                            <SelectItem key={company.id} value={company.id}>
                              {company.company_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button onClick={() => setNewMemberDialogOpen(true)}>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Add Member
                      </Button>
                    </div>
                  </div>

                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Designation</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {teamMembers.map((member) => (
                          <TableRow key={member.id}>
                            <TableCell>{member.full_name}</TableCell>
                            <TableCell>{member.email}</TableCell>
                            <TableCell>{member.designation}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveTeamMember(member.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {teamMembers.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center text-muted-foreground">
                              No team members added yet
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground">Select a company to manage team members</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pitch-decks">
          <Card>
            <CardHeader>
              <CardTitle>Pitch Deck Management</CardTitle>
              <CardDescription>Create and manage company pitch decks</CardDescription>
            </CardHeader>
            <CardContent>
              {selectedCompany ? (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">{selectedCompany.company_name} - Pitch Deck</h3>
                    <div className="flex gap-2">
                      <Select 
                        value={selectedCompany.id} 
                        onValueChange={(value) => {
                          const company = companies.find(c => c.id === value);
                          if (company) {
                            setSelectedCompany(company);
                            setFormData(company);
                            setEditMode(false);
                          }
                        }}
                      >
                        <SelectTrigger className="w-[240px]">
                          <SelectValue placeholder="Select company" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map(company => (
                            <SelectItem key={company.id} value={company.id}>
                              {company.company_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        variant={editMode ? "secondary" : "default"}
                        onClick={() => setEditMode(!editMode)}
                      >
                        {editMode ? "Cancel" : "Edit"}
                      </Button>
                    </div>
                  </div>

                  <div className="grid gap-6">
                    <div className="space-y-6">
                      {/* Company Overview Section */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-xl">Company Overview</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <div>
                              <Label className="text-base font-semibold">Description</Label>
                              <Textarea 
                                placeholder="Brief description of the company..."
                                value={formData.description || ''}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                disabled={!editMode}
                                className="mt-2"
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Investment Information Section */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-xl">Investment Information</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-6">
                            <div>
                              <Label className="text-base font-semibold">Investment Highlights</Label>
                              <Textarea 
                                placeholder="Key investment highlights (one per line)..."
                                value={Array.isArray(formData.investment_highlights) ? formData.investment_highlights.join('\n') : ''}
                                onChange={(e) => handleInputChange('investment_highlights', e.target.value.split('\n').filter(Boolean))}
                                disabled={!editMode}
                                className="mt-2"
                                rows={5}
                              />
                              {!editMode && Array.isArray(formData.investment_highlights) && formData.investment_highlights.length > 0 && (
                                <ul className="list-disc list-inside mt-2 space-y-1">
                                  {formData.investment_highlights.map((highlight, index) => (
                                    <li key={index} className="text-gray-700">{highlight}</li>
                                  ))}
                                </ul>
                              )}
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                              <div>
                                <Label className="text-base font-semibold">Funding Goal</Label>
                                <div className="flex items-center mt-2">
                                  <span className="text-gray-500 mr-2">$</span>
                                  <Input 
                                    type="number"
                                    placeholder="Amount needed..."
                                    value={formData.funding_goal || ''}
                                    onChange={(e) => handleInputChange('funding_goal', parseFloat(e.target.value))}
                                    disabled={!editMode}
                                  />
                                </div>
                              </div>
                              <div>
                                <Label className="text-base font-semibold">Market Size</Label>
                                <Textarea 
                                  placeholder="Describe the target market and its size..."
                                  value={formData.market_size || ''}
                                  onChange={(e) => handleInputChange('market_size', e.target.value)}
                                  disabled={!editMode}
                                  className="mt-2"
                                />
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Business Strategy Section */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-xl">Business Strategy</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-6">
                            <div>
                              <Label className="text-base font-semibold">Problem Statement</Label>
                              <Textarea 
                                placeholder="What problem does the company solve?"
                                value={formData.problem_statement || ''}
                                onChange={(e) => handleInputChange('problem_statement', e.target.value)}
                                disabled={!editMode}
                                className="mt-2"
                              />
                            </div>

                            <div>
                              <Label className="text-base font-semibold">Solution</Label>
                              <Textarea 
                                placeholder="How does the company solve this problem?"
                                value={formData.solution_description || ''}
                                onChange={(e) => handleInputChange('solution_description', e.target.value)}
                                disabled={!editMode}
                                className="mt-2"
                              />
                            </div>

                            <div>
                              <Label className="text-base font-semibold">Competitive Edge</Label>
                              <Textarea 
                                placeholder="What makes the company unique? (one advantage per line)"
                                value={Array.isArray(formData.competitive_edge) ? formData.competitive_edge.join('\n') : ''}
                                onChange={(e) => handleInputChange('competitive_edge', e.target.value.split('\n').filter(Boolean))}
                                disabled={!editMode}
                                className="mt-2"
                                rows={4}
                              />
                              {!editMode && Array.isArray(formData.competitive_edge) && formData.competitive_edge.length > 0 && (
                                <ul className="list-disc list-inside mt-2 space-y-1">
                                  {formData.competitive_edge.map((edge, index) => (
                                    <li key={index} className="text-gray-700">{edge}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Business Model & Financials Section */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-xl">Business Model & Financials</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-6">
                            <div>
                              <Label className="text-base font-semibold">Business Model</Label>
                              <Textarea 
                                placeholder="How does the company make money? Include revenue streams, pricing strategy, etc."
                                value={formData.business_model || ''}
                                onChange={(e) => handleInputChange('business_model', e.target.value)}
                                disabled={!editMode}
                                className="mt-2"
                                rows={6}
                              />
                            </div>

                            <div>
                              <Label className="text-base font-semibold">Financial Projections</Label>
                              <Textarea 
                                placeholder="Financial forecasts and projections..."
                                value={formData.financial_projections || ''}
                                onChange={(e) => handleInputChange('financial_projections', e.target.value)}
                                disabled={!editMode}
                                className="mt-2"
                                rows={6}
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Traction & Team Section */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-xl">Traction & Team</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-6">
                            <div>
                              <Label className="text-base font-semibold">Traction & Milestones</Label>
                              <Textarea 
                                placeholder="Current progress, achievements, and key milestones..."
                                value={formData.traction || ''}
                                onChange={(e) => handleInputChange('traction', e.target.value)}
                                disabled={!editMode}
                                className="mt-2"
                                rows={4}
                              />
                            </div>

                            <div>
                              <Label className="text-base font-semibold">Team</Label>
                              <Textarea 
                                placeholder="Key team members, their roles, and relevant experience..."
                                value={formData.team_members || ''}
                                onChange={(e) => handleInputChange('team_members', e.target.value)}
                                disabled={!editMode}
                                className="mt-2"
                                rows={4}
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {editMode && (
                      <div className="flex justify-end gap-2 sticky bottom-4 bg-white p-4 border rounded-lg shadow-lg">
                        <Button variant="outline" onClick={() => setEditMode(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleSave}>
                          Save Changes
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground">Select a company to manage pitch deck</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Member Dialog */}
      <Dialog open={newMemberDialogOpen} onOpenChange={setNewMemberDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>
              Add a new team member to {selectedCompany?.company_name}. These team members will be displayed on the company details page. Only registered users can be added as team members.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="user" className="text-right">
                User
              </Label>
              <Select 
                onValueChange={(value) => {
                  const selectedUser = registeredUsers.find(user => user.id === value);
                  if (selectedUser) {
                    setNewMemberData({
                      ...newMemberData,
                      email: selectedUser.email,
                      full_name: selectedUser.full_name
                    });
                  }
                }}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {registeredUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="designation" className="text-right">
                Designation
              </Label>
              <Input
                id="designation"
                className="col-span-3"
                value={newMemberData.designation}
                onChange={(e) =>
                  setNewMemberData({ ...newMemberData, designation: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleAddTeamMember}>Add Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Company Dialog */}
      <Dialog open={newCompanyDialogOpen} onOpenChange={setNewCompanyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Company</DialogTitle>
            <DialogDescription>
              Add a new company to manage pitch decks and team members.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="company_name" className="text-right">
                Company Name
              </Label>
              <Input
                id="company_name"
                className="col-span-3"
                value={newCompanyData.company_name}
                onChange={(e) => setNewCompanyData(prev => ({ ...prev, company_name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="tagline" className="text-right">
                Tagline
              </Label>
              <Input
                id="tagline"
                className="col-span-3"
                value={newCompanyData.tagline}
                onChange={(e) => setNewCompanyData(prev => ({ ...prev, tagline: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Description
              </Label>
              <Textarea
                id="description"
                className="col-span-3"
                value={newCompanyData.description}
                onChange={(e) => setNewCompanyData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="development_stage" className="text-right">
                Stage
              </Label>
              <Select
                value={newCompanyData.development_stage}
                onValueChange={(value: 'development' | 'operation') => 
                  setNewCompanyData(prev => ({ ...prev, development_stage: value }))
                }
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="development">Development</SelectItem>
                  <SelectItem value="operation">Operation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="industry_type" className="text-right">
                Industry
              </Label>
              <Input
                id="industry_type"
                className="col-span-3"
                value={newCompanyData.industry_type}
                onChange={(e) => setNewCompanyData(prev => ({ ...prev, industry_type: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="website" className="text-right">
                Website
              </Label>
              <Input
                id="website"
                className="col-span-3"
                value={newCompanyData.website}
                onChange={(e) => setNewCompanyData(prev => ({ ...prev, website: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="contact_email" className="text-right">
                Contact Email
              </Label>
              <Input
                id="contact_email"
                className="col-span-3"
                value={newCompanyData.contact_email}
                onChange={(e) => setNewCompanyData(prev => ({ ...prev, contact_email: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleAddCompany}>Add Company</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default CompanyManager;

